import JourneyCardSkeleton from '@/components/purchase/journey/JourneyCardSkeleton';
import JourneyList from '@/components/purchase/journey/JourneyList';
import { outboundOrInboundJourneysLoadingSelector } from '@/features/loading/loadingSelectors';
import {
  getInboundExchangeOffers,
  getInboundJourneys,
  getOutboundExchangeOffers,
  getOutboundJourneys,
  setSelectedOutboundJourney,
} from '@/features/purchase/purchaseActions';
import { outboundDataSelector } from '@/features/purchase/purchaseSelectors';
import { useChangeBookingSelection } from '@/hooks/useChangeBookingSelection';
import { useSearchWidget } from '@/hooks/useSearchWidget';
import { TransText } from '@/i18n/trans/text';
import { useDispatch, useSelector } from '@/store/utils';
import type { ErrorDTO } from '@/types/dto/agent';
import { buildSearchOffersStopParams } from '@/utils/buildSearchOffersStopParams';
import { formatFullDate } from '@/utils/dateTime';
import { GTM_EVENT } from '@/utils/gtm';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

const OutboundJourneyList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const outboundData = useSelector(outboundDataSelector);
  const loading = useSelector(outboundOrInboundJourneysLoadingSelector);
  const { currentSearchFormValues } = useSearchWidget();
  const changeBookingSelection = useChangeBookingSelection();

  useEffect(() => {
    // If no journeys in Redux, check if we have search form values to fetch
    if (!outboundData.journeys && !loading) {
      // Change-booking edge case: the user picked ONLY the return trip
      // (no outbound) — `handleSubmit` routes everyone to /outbound/journey
      // by default, but the outbound exchange dispatch would early-return
      // with zero fulfillment IDs. Fire the inbound exchange search with
      // the form values as-is (return-only is a one-way search of the
      // return leg's natural route — no origin/destination reversal — and
      // there's no `returnSearchParameters` because there's no outbound
      // leg to coordinate with), THEN redirect to /purchase/inbound/journey
      // so the user lands on a list that already has results (or is
      // loading them), not on a blank fallback.
      if (
        changeBookingSelection.changeFlow === 'changeTrips' &&
        changeBookingSelection.hasSelection &&
        !changeBookingSelection.selectedOutboundTrip &&
        changeBookingSelection.selectedInboundTrip &&
        currentSearchFormValues?.origin?.value &&
        currentSearchFormValues?.destination?.value
      ) {
        dispatch(
          getInboundExchangeOffers({
            ...buildSearchOffersStopParams(
              currentSearchFormValues.origin,
              currentSearchFormValues.destination
            ),
            departureTime: formatFullDate(
              currentSearchFormValues.departureDate
            ),
            promoCodes: currentSearchFormValues.promoCodes,
          })
        )
          .unwrap()
          .catch((err) => {
            if (err.response?.data && 'errors' in err.response.data) {
              err.response.data.errors.forEach((error: ErrorDTO) =>
                toast.error(error.message)
              );
            }
          });
        navigate('/purchase/inbound/journey', { replace: true });
        return;
      }
      if (
        currentSearchFormValues?.origin?.value &&
        currentSearchFormValues?.destination?.value
      ) {
        // Change-booking branch: dispatch the modification-flow exchange
        // search (Modifications_FindExchangeOffers). The thunk reads the
        // bookingId, fulfillmentIds and passengers from the change-booking
        // selection in redux. Regular customers still go through the plain
        // search path below.
        const isChangeTrips =
          changeBookingSelection.hasSelection &&
          changeBookingSelection.changeFlow === 'changeTrips';

        // Only the plain search takes `passengers`, so the two flows can share
        // the params but not the dispatch.
        const outboundParams = {
          ...buildSearchOffersStopParams(
            currentSearchFormValues.origin,
            currentSearchFormValues.destination
          ),
          departureTime: formatFullDate(currentSearchFormValues.departureDate),
          promoCodes: currentSearchFormValues.promoCodes,
          ...(currentSearchFormValues.returnDate
            ? {
                returnSearchParameters: {
                  inwardReturnDate: formatFullDate(
                    currentSearchFormValues.returnDate
                  ),
                },
              }
            : {}),
        };

        // BE returns 4xx on validation failure — axios throws, the thunk
        // rejects, `.unwrap()` rethrows. Toast each search call's error
        // independently so one direction's failure doesn't suppress the
        // other's result.
        (isChangeTrips
          ? dispatch(getOutboundExchangeOffers(outboundParams))
          : dispatch(
              getOutboundJourneys({
                ...outboundParams,
                passengers: currentSearchFormValues.passengers,
              })
            )
        )
          .unwrap()
          .catch((err) => {
            if ('errors' in err.response.data) {
              err.response.data.errors.forEach((error: ErrorDTO) =>
                toast.error(error.message)
              );
            }
          });

        if (currentSearchFormValues.returnDate) {
          const inboundParams = {
            ...buildSearchOffersStopParams(
              currentSearchFormValues.destination,
              currentSearchFormValues.origin
            ),
            departureTime: formatFullDate(currentSearchFormValues.returnDate),
            promoCodes: currentSearchFormValues.promoCodes,
            returnSearchParameters: {
              outwardTripDate: formatFullDate(
                currentSearchFormValues.departureDate
              ),
            },
          };

          (isChangeTrips
            ? dispatch(getInboundExchangeOffers(inboundParams))
            : dispatch(
                getInboundJourneys({
                  ...inboundParams,
                  passengers: currentSearchFormValues.passengers,
                })
              )
          )
            .unwrap()
            .catch((err) => {
              if ('errors' in err.response.data) {
                err.response.data.errors.forEach((error: ErrorDTO) =>
                  toast.error(error.message)
                );
              }
            });
        }
      } else {
        // No form values, redirect back to search
        navigate('/purchase');
      }
    }
  }, [
    outboundData.journeys,
    loading,
    currentSearchFormValues,
    changeBookingSelection.changeFlow,
    changeBookingSelection.hasSelection,
    changeBookingSelection.selectedOutboundTrip,
    changeBookingSelection.selectedInboundTrip,
    dispatch,
    navigate,
  ]);

  // Show skeleton while loading and no journeys yet
  if (loading && !outboundData.journeys) {
    return (
      <div className="mx-auto w-full max-w-laptop p-4">
        <div className="mx-auto max-w-mobile">
          <div className="grid grid-cols-[1fr_auto] gap-y-4">
            {[1, 2, 3, 4].map((i) => (
              <JourneyCardSkeleton key={i} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <JourneyList
      {...outboundData}
      setSelectedJourney={setSelectedOutboundJourney}
      onContinue={() => navigate('/purchase/outbound/offer')}
      journeySelectedEvent={GTM_EVENT.OUTBOUND_JOURNEY_SELECTED}
      title={
        outboundData.journeys && outboundData.journeys.length > 0 ? (
          <TransText i18nKey="selectOutboundTrip" />
        ) : undefined
      }
    />
  );
};

export default OutboundJourneyList;
